import type {
  AddSignRecipientRequest,
  ApiSuccess,
  DeclineSignatureRequest,
  PublicSignView,
  SetSignFieldsRequest,
  SetSignRecipientsRequest,
  CreateSignCategoryRequest,
  ListSignDocumentsQuery,
  SignCategory,
  SignCategoryLogEntry,
  SignTag,
  SignDocumentCreated,
  SignDocumentDetail,
  SignDocumentSummary,
  SignInboxEntry,
  SignSourceFiles,
  SignPersonResult,
  DelegateSignatureRequest,
  SubmitSignatureRequest,
} from '@todo/shared';
import { api, get, getPaged, post, put } from './client';

/** File -> plain base64 (no data: prefix), chunked so big PDFs don't
 *  blow the call stack. */
async function fileToBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

/** /api/sign - CG Sign. The /s/:token calls are the PUBLIC signing side:
 *  they work with no session, the link's token is the authentication. */
export const signApi = {
  /**
   * The list, filtered SERVER-side. Multi-value filters travel
   * comma-separated - they are query strings, not bodies - and an empty
   * array is omitted so it never narrows anything to nothing.
   */
  list(query: ListSignDocumentsQuery = {}): Promise<ApiSuccess<SignDocumentSummary[]>> {
    return getPaged<SignDocumentSummary[]>('/sign/documents', {
      ...(query.search ? { search: query.search } : {}),
      ...(query.status?.length ? { status: query.status.join(',') } : {}),
      ...(query.categoryIds?.length ? { categoryIds: query.categoryIds.join(',') } : {}),
      ...(query.tagIds?.length ? { tagIds: query.tagIds.join(',') } : {}),
      ...(query.departments?.length ? { departments: query.departments.join(',') } : {}),
      ...(query.ownerIds?.length ? { ownerIds: query.ownerIds.join(',') } : {}),
      ...(query.scope ? { scope: query.scope } : {}),
      page: query.page ?? 1,
      pageSize: query.pageSize ?? 50,
    });
  },

  /** Envelopes waiting on MY signature (matched by my email). */
  /** What the header filter row offers - served, not derived from the
   *  page on screen, which could only ever offer what you can see. */
  filters(): Promise<{ categories: SignCategory[]; tags: SignTag[]; departments: string[] }> {
    return get<{ categories: SignCategory[]; tags: SignTag[]; departments: string[] }>('/sign/filters');
  },

  // ---- categories: edit / merge / delete, and the log ----
  renameCategory(id: string, name: string): Promise<SignCategory> {
    return api
      .put<ApiSuccess<SignCategory>>(`/sign/categories/${id}`, { name })
      .then((r) => r.data.data);
  },

  categoryLog(): Promise<SignCategoryLogEntry[]> {
    return get<SignCategoryLogEntry[]>('/sign/category-log');
  },

  // ---- tags ----
  tags(): Promise<SignTag[]> {
    return get<SignTag[]>('/sign/tags');
  },

  renameTag(id: string, name: string): Promise<SignTag> {
    return api.put<ApiSuccess<SignTag>>(`/sign/tags/${id}`, { name }).then((r) => r.data.data);
  },

  async removeTag(id: string): Promise<void> {
    await api.delete(`/sign/tags/${id}`);
  },

  /** Replace the envelope's whole tag set (sender only). */
  setTags(id: string, tags: string[]): Promise<SignDocumentSummary> {
    return api
      .put<ApiSuccess<SignDocumentSummary>>(`/sign/documents/${id}/tags`, { tags })
      .then((r) => r.data.data);
  },

  categories(): Promise<SignCategory[]> {
    return get<SignCategory[]>('/sign/categories');
  },

  /** Idempotent by name: an existing category comes back rather than an error. */
  createCategory(body: CreateSignCategoryRequest): Promise<SignCategory> {
    return post<SignCategory>('/sign/categories', body);
  },

  /** `mergeInto`: move its documents there instead of leaving them
   *  uncategorised. */
  async removeCategory(id: string, mergeInto?: string): Promise<void> {
    await api.delete(`/sign/categories/${id}`, mergeInto ? { params: { mergeInto } } : undefined);
  },

  setCategory(id: string, categoryId: string | null): Promise<SignDocumentSummary> {
    return api
      .put<ApiSuccess<SignDocumentSummary>>(`/sign/documents/${id}/category`, { categoryId })
      .then((r) => r.data.data);
  },

  /** People in ANY organisation, for inviting a signer from another one. */
  people(search: string, signal?: AbortSignal, limit = 8): Promise<SignPersonResult[]> {
    return api
      .get<ApiSuccess<SignPersonResult[]>>('/sign/people', { params: { search, limit }, signal })
      .then((r) => r.data.data);
  },

  inbox(): Promise<SignInboxEntry[]> {
    return get<SignInboxEntry[]>('/sign/inbox');
  },

  detail(id: string): Promise<SignDocumentDetail> {
    return get<SignDocumentDetail>(`/sign/documents/${id}`);
  },

  /**
   * One or more files (PDF/PNG/JPEG/.xlsx/.csv), merged server-side into
   * one PDF in LIST ORDER. Travels as base64 JSON under
   * application/octet-stream so the app-wide 1 MB json limit does not
   * apply.
   *
   * A spreadsheet is RENDERED to pages, so the reply carries
   * `conversionWarnings` - everything the rendering could not carry over.
   * Show them; they are the sender's last chance to check before the
   * envelope goes out.
   */
  async create(
    title: string,
    message: string,
    files: File[],
    categoryId?: string | null,
    tags: string[] = [],
    /** Tick "Create approval sheet": the text after "Approval Sheet - ". */
    approvalSheetTitle?: string,
  ): Promise<SignDocumentCreated> {
    const parts = await Promise.all(
      files.map(async (f) => ({ fileName: f.name, data: await fileToBase64(f) })),
    );
    const body = JSON.stringify({
      title,
      ...(message ? { message } : {}),
      ...(categoryId ? { categoryId } : {}),
      ...(tags.length ? { tags } : {}),
      ...(approvalSheetTitle ? { approvalSheetTitle } : {}),
      files: parts,
    });
    const res = await api.post<ApiSuccess<SignDocumentCreated>>('/sign/documents', body, {
      headers: { 'Content-Type': 'application/octet-stream' },
    });
    return res.data.data;
  },

  /**
   * The PDF a file WOULD become, without creating anything - the same
   * conversion the real send runs (a sheet is drawn to pages, an image is
   * fitted to A4). Raw body, so the 1 MB json limit does not apply.
   */
  async previewFile(file: File): Promise<ArrayBuffer> {
    const res = await api.post<ArrayBuffer>(
      `/sign/preview?filename=${encodeURIComponent(file.name)}`,
      file,
      {
        headers: { 'Content-Type': 'application/octet-stream' },
        responseType: 'arraybuffer',
      },
    );
    return res.data;
  },

  setRecipients(id: string, body: SetSignRecipientsRequest): Promise<SignDocumentSummary> {
    return api
      .put<ApiSuccess<SignDocumentSummary>>(`/sign/documents/${id}/recipients`, body)
      .then((r) => r.data.data);
  },

  /** ONE more signer on a draft. Additive - unlike setRecipients it leaves
   *  existing recipients, their links and their placed boxes alone. */
  addRecipient(id: string, body: AddSignRecipientRequest): Promise<SignDocumentSummary> {
    return api
      .post<ApiSuccess<SignDocumentSummary>>(`/sign/documents/${id}/recipients`, body)
      .then((r) => r.data.data);
  },

  /** More files onto a draft envelope, merged AFTER the existing pages.
   *  Same octet-stream carriage as create - base64 outgrows the json limit. */
  async addFiles(id: string, files: File[]): Promise<SignDocumentCreated> {
    const parts = await Promise.all(
      files.map(async (f) => ({ fileName: f.name, data: await fileToBase64(f) })),
    );
    const res = await api.post<ApiSuccess<SignDocumentCreated>>(
      `/sign/documents/${id}/files`,
      JSON.stringify({ files: parts }),
      { headers: { 'Content-Type': 'application/octet-stream' } },
    );
    return res.data.data;
  },

  /** The original PDF's bytes, for the placement editor's renderer. */
  fileBytes(id: string): Promise<ArrayBuffer> {
    return api
      .get<ArrayBuffer>(`/sign/documents/${id}/file`, { responseType: 'arraybuffer' })
      .then((r) => r.data);
  },

  async setFields(id: string, body: SetSignFieldsRequest): Promise<void> {
    await api.put(`/sign/documents/${id}/fields`, body);
  },

  /** The SEALED PDF's bytes, for the in-app viewer once completed. */
  signedFileBytes(id: string): Promise<ArrayBuffer> {
    return api
      .get<ArrayBuffer>(`/sign/documents/${id}/signed-file`, { responseType: 'arraybuffer' })
      .then((r) => r.data);
  },

  /**
   * The Certificate of Completion as its own file. Returns the bytes and
   * the server's suggested name, so the caller can either save it or hand
   * it to the in-app viewer.
   */
  async certificate(id: string): Promise<{ bytes: ArrayBuffer; fileName: string }> {
    const res = await api.get<ArrayBuffer>(`/sign/documents/${id}/certificate`, {
      responseType: 'arraybuffer',
    });
    const disposition = String(res.headers['content-disposition'] ?? '');
    const match = /filename="?([^";]+)"?/i.exec(disposition);
    return { bytes: res.data, fileName: match?.[1] ?? 'certificate-of-completion.pdf' };
  },

  /** The signing progress so far as a PDF (signatures drawn, "Awaiting <name>" on the rest). */
  async progressPdf(id: string): Promise<{ bytes: ArrayBuffer; fileName: string }> {
    const res = await api.get<ArrayBuffer>(`/sign/documents/${id}/progress-pdf`, {
      responseType: 'arraybuffer',
    });
    const disposition = String(res.headers['content-disposition'] ?? '');
    const match = /filename="?([^";]+)"?/i.exec(disposition);
    return { bytes: res.data, fileName: match?.[1] ?? 'signing-progress.pdf' };
  },

  /** Rebuild one sealed PDF without the old trailing certificate page. */
  reseal(id: string): Promise<SignDocumentSummary> {
    return post<SignDocumentSummary>(`/sign/documents/${id}/reseal`);
  },

  /** The same sweep across every completed envelope (Super Admin). */
  resealAll(): Promise<{
    rebuilt: number; failed: Array<{ id: string; title: string; reason: string }>;
  }> {
    return post<{
      rebuilt: number; failed: Array<{ id: string; title: string; reason: string }>;
    }>('/sign/documents/reseal');
  },

  send(id: string): Promise<SignDocumentSummary> {
    return post<SignDocumentSummary>(`/sign/documents/${id}/send`);
  },

  /** SENT -> back to DRAFT; the API refuses once anyone has signed. */
  recall(id: string): Promise<SignDocumentSummary> {
    return post<SignDocumentSummary>(`/sign/documents/${id}/recall`);
  },

  /** The sender's file list for a draft; managed=false for an envelope made before files were kept. */
  sourceFiles(id: string): Promise<SignSourceFiles> {
    return get<SignSourceFiles>(`/sign/documents/${id}/source-files`);
  },

  /** Keep these files, in this order; any left out are removed. Draft only. */
  arrangeFiles(id: string, order: string[]): Promise<SignDocumentCreated> {
    return put<SignDocumentCreated>(`/sign/documents/${id}/source-files`, { order });
  },

  /** A reason is mandatory: it lands in the activity trail. */
  void(id: string, reason: string): Promise<SignDocumentSummary> {
    return post<SignDocumentSummary>(`/sign/documents/${id}/void`, { reason });
  },

  /** VOIDED -> active again (sent or draft, whichever it was). Reason mandatory. */
  reopen(id: string, reason: string): Promise<SignDocumentSummary> {
    return post<SignDocumentSummary>(`/sign/documents/${id}/reopen`, { reason });
  },

  async removeDraft(id: string): Promise<void> {
    await api.delete(`/sign/documents/${id}`);
  },

  // ---- public (token) ----

  publicView(token: string): Promise<PublicSignView> {
    return get<PublicSignView>(`/sign/s/${token}`);
  },

  /** The PDF's bytes for a signing link, for the page's pdf.js renderer. */
  publicFileBytes(token: string): Promise<ArrayBuffer> {
    return api
      .get<ArrayBuffer>(`/sign/s/${token}/file`, { responseType: 'arraybuffer' })
      .then((r) => r.data);
  },

  /** Hand this signature to a colleague in the same department. */
  delegate(token: string, body: DelegateSignatureRequest): Promise<{ delegated: true }> {
    return post<{ delegated: true }>(`/sign/s/${token}/delegate`, body);
  },

  sign(token: string, body: SubmitSignatureRequest): Promise<void> {
    return post<void>(`/sign/s/${token}/sign`, body);
  },

  decline(token: string, body: DeclineSignatureRequest): Promise<void> {
    return post<void>(`/sign/s/${token}/decline`, body);
  },
};
